import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../componentes/Plantilla/Plantilla';
import styles from './Nosotros.module.css';
import Breadcrumbs from '../componentes/UI/Breadcrumbs';
import logoSeima from '../recursos/imagenes/logo.webp';

/* ── Hook reutilizable para animar en scroll ── */
const useReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* ── Contador animado ── */
const Contador = ({ hasta, sufijo = '', duracion = 1800 }) => {
  const [valor, setValor] = useState(0);
  const [ref, visible] = useReveal(0.3);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progreso = Math.min((ts - start) / duracion, 1);
      setValor(Math.floor(progreso * hasta));
      if (progreso < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, hasta, duracion]);
  return <span ref={ref}>{valor}{sufijo}</span>;
};

/* ── Datos ── */
const valores = [
  {
    icono: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    titulo: 'Integridad',
    texto: 'Actuamos con transparencia y honestidad en cada decisión, construyendo relaciones de confianza duraderas con nuestros clientes y socios.',
  },
  {
    icono: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    titulo: 'Puntualidad',
    texto: 'Cumplimos con los tiempos comprometidos porque entendemos que cada minuto de operación cuenta para nuestros clientes industriales.',
  },
  {
    icono: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    titulo: 'Honestidad',
    texto: 'Decimos lo que pensamos y prometemos lo que podemos cumplir. La honestidad es el cimiento de cada relación comercial que construimos.',
  },
  {
    icono: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    titulo: 'Trabajo en equipo',
    texto: 'Nos esforzamos juntos cada día, combinando experiencia técnica, vocación de servicio y un compromiso compartido con la excelencia.',
  },
];

const hitos = [
  { año: '1991', titulo: 'Fundación', texto: 'Nace como Comercializadora Internacional con la misión de importar y comercializar equipos profesionales de limpieza y mantenimiento para la industria guatemalteca.', imagen: '/imagenes/historia/seima-lavadoras-paquete.webp' },
  { año: '1993', titulo: 'SEIMA, S.A.', texto: 'La empresa se constituye formalmente como SEIMA, S.A., consolidando su presencia en el mercado y expandiendo su portafolio de productos y servicios.', imagen: logoSeima },
  { año: '2000s', titulo: 'Expansión', texto: 'Ampliamos alianzas con marcas europeas y americanas líderes, fortaleciendo nuestra capacidad de ofrecer soluciones integrales a la industria.', imagen: '/imagenes/historia/seima-camionetas-parqueadas.webp' },
  { año: 'Hoy', titulo: 'Liderazgo regional', texto: 'Con más de 30 años de experiencia, proyectamos nuestra expansión hacia El Salvador, Honduras y toda Centroamérica.', imagen: '/imagenes/historia/seima-edificio.webp' },
];

/* ── Componente principal ── */
const Nosotros = () => {
  const [heroRef, heroVisible] = useReveal(0.05);
  const [quienesRef, quienesVisible] = useReveal(0.1);
  const [valoresRef] = useReveal(0.08);
  const [mvRef, mvVisible] = useReveal(0.1);
  const [valorActivo, setValorActivo] = useState(0);

  // Lógica Sticky Scroll Timeline
  const timelineContainerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [videoActivo, setVideoActivo] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineContainerRef.current) return;
      const { top, height } = timelineContainerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (top > 0) {
        setScrollProgress(0);
        return;
      }
      
      const maxScroll = height - windowHeight;
      const currentScroll = -top;
      
      if (currentScroll >= maxScroll) {
        setScrollProgress(1);
        return;
      }
      
      const progress = currentScroll / maxScroll;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <Layout>

      {/* ══════════════════════════════════
          HERO
      ══════════════════════════════════ */}
      <section className={styles.hero} ref={heroRef}>
        <div className={styles.heroFondo}>
          <img
            src="/imagenes/historia/seima-edificio.webp"
            alt="Edificio SEIMA"
            className={styles.heroImg}
          />
          <div className={styles.heroOverlay} />
        </div>

        <div className={`container ${styles.heroContenido} ${heroVisible ? styles.heroVisible : ''}`}>
          <Breadcrumbs 
            paths={[
              { label: 'Inicio', to: '/' },
              { label: 'Nosotros' }
            ]}
          />
          <h1 className={styles.heroTitulo}>
            Nuestra<br />
            <span className={styles.heroAcento}>Historia</span>
          </h1>
          <div className={styles.lineaTitulo} />
          
          <p className={styles.heroSub}>
            Más de 30 años siendo el aliado estratégico de la industria guatemalteca
            en equipos de limpieza, lavandería y mantenimiento.
          </p>

          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}><Contador hasta={30} sufijo="+" /></span>
              <span className={styles.heroStatLabel}>Años de experiencia</span>
            </div>
            <div className={styles.heroStatDiv} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}><Contador hasta={500} sufijo="+" /></span>
              <span className={styles.heroStatLabel}>Clientes atendidos</span>
            </div>
            <div className={styles.heroStatDiv} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}><Contador hasta={8} /></span>
              <span className={styles.heroStatLabel}>Marcas representadas</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className={styles.scrollHint} aria-hidden>
          <span className={styles.scrollLinea} />
          <span className={styles.scrollTexto}>scroll</span>
        </div>
      </section>

      {/* ══════════════════════════════════
          ¿QUIÉNES SOMOS?
      ══════════════════════════════════ */}
      <section
        className={`${styles.seccion} ${styles.quienesSomos}`}
        ref={quienesRef}
      >
        <div className="container">
          <div className={`${styles.quienesGrid} ${quienesVisible ? styles.revealActivo : ''}`}>
            <div className={styles.quienesTexto}>
              <div className={styles.encabezado} style={{ marginBottom: '32px' }}>
                <div className={styles.lineaAcento} />
                <h2 className={styles.seccionTitulo}>¿Quiénes somos?</h2>
              </div>
              <p>
                Somos un grupo de profesionales que nos esforzamos todos los días por
                solucionar las necesidades de <strong>mantenimiento y limpieza industrial
                y comercial</strong>. Brindamos equipos Europeos y Americanos diseñados
                específicamente para cada necesidad.
              </p>
              <p>
                Garantizamos servicio, reparación y suministro continuo para cada equipo.
                Desde <strong>1991</strong> contamos con la experiencia que nos da la
                seguridad de asesorar y solucionar los problemas de nuestros clientes.
              </p>
              <p>
                Representamos marcas de prestigio mundial como <strong>DOMUS</strong>, <strong>MAYTAG</strong>, <strong>Goodway</strong>, <strong>Powr-Flite</strong>, <strong>NLB Corp</strong>, <strong>MetroVac</strong> y <strong>Delfin</strong>. Nuestro catálogo abarca desde aspiradoras industriales y barredoras, hasta hidrolavadoras de alta presión y maquinaria de lavandería, enfocándonos siempre en ofrecer equipos que duran más, consumen menos y operan sin fallos.
              </p>
              <Link to="/#contact" className="btn btn-primary" style={{ marginTop: '8px', width: 'fit-content' }}>
                Contáctanos
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                  strokeWidth={2} stroke="currentColor" width="16" height="16">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>

            <div className={styles.quienesImgWrap} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div className={styles.videoCard}>
                <div className={styles.iframeWrapper}>
                  {videoActivo ? (
                    <div style={{ width: '100%', height: '100%', backgroundColor: '#000' }} />
                  ) : (
                    <iframe 
                      src={`https://www.youtube.com/embed/-_IN4xbyKwc?rel=0`} 
                      title="Powr Flite Floor Machines Expert Recommendations"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  )}
                </div>
                <div 
                  className={styles.videoInfo} 
                  onClick={() => setVideoActivo(true)}
                  title="Haga clic para expandir video"
                >
                  <h3 className={styles.videoTitle}>Powr Flite Floor Machines Expert Recommendations</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          LÍNEA DE TIEMPO (STICKY SCROLL)
      ══════════════════════════════════ */}
      <section className={styles.timelineSeccionWrapper} ref={timelineContainerRef}>
        <div className={styles.timelineSticky}>
          <div className="container" style={{ height: '100%', display: 'flex', flexDirection: 'column', paddingTop: '80px', paddingBottom: '40px' }}>
            <div className={styles.encabezado} style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h2 className={styles.heroTitulo} style={{ color: 'var(--text-primary)', margin: '0 auto 10px', fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>Nuestra historia</h2>
              <div className={styles.lineaTitulo} style={{ margin: '0 auto' }} />
            </div>

            <div className={styles.timelineCentral}>
              <div className={styles.timelineLiniaSticky} />
              
              {hitos.map((h, i) => {
                // Cálculo de distancia continua
                // El progreso va de 0 a 1. Lo mapeamos de 0 a hitos.length - 1
                const currentVirtualIndex = scrollProgress * (hitos.length - 1);
                const distance = i - currentVirtualIndex;
                const absDist = Math.abs(distance);
                
                // Animaciones interpoladas
                const translateY = distance * 220; // 220px de separación entre tarjetas
                const scale = Math.max(1 - absDist * 0.4, 0.5); // Escala de 1 a 0.5
                const opacity = Math.max(1 - absDist * 0.8, 0); // Opacidad de 1 a 0
                const zIndex = Math.floor(100 - absDist * 10);
                
                // Mostrar contenido solo si está cerca del centro
                const isNearCenter = absDist < 0.5;
                const layoutRow = i % 2 === 0 ? 'row' : 'row-reverse';

                return (
                  <div 
                    key={i} 
                    className={styles.hitoStickyContainer}
                    style={{
                      transform: `translateY(calc(-50% + ${translateY}px)) scale(${scale})`,
                      opacity,
                      zIndex
                    }}
                  >
                    {/* Contenido Completo de la Tarjeta */}
                    <div 
                      className={styles.hitoContenidoCompleto}
                      style={{ 
                        opacity: isNearCenter ? 1 : 0.4, 
                        flexDirection: layoutRow,
                        pointerEvents: isNearCenter ? 'auto' : 'none',
                        filter: isNearCenter ? 'blur(0px)' : 'blur(4px)',
                        transition: 'opacity 0.2s, filter 0.2s'
                      }}
                    >
                      <div className={styles.hitoMitadTexto}>
                        <div className={styles.hitoAñoDestacado}>{h.año}</div>
                        <h3 className={styles.hitoTituloSticky}>{h.titulo}</h3>
                        <p className={styles.hitoTextoSticky}>{h.texto}</p>
                      </div>
                      <div className={styles.hitoMitadImagen} style={{ backgroundColor: h.titulo === 'SEIMA, S.A.' ? '#1e1e1e' : 'transparent' }}>
                        <img 
                          src={h.imagen} 
                          alt={h.titulo} 
                          style={{ 
                            objectFit: h.titulo === 'SEIMA, S.A.' ? 'contain' : 'cover',
                            padding: h.titulo === 'SEIMA, S.A.' ? '40px' : '0'
                          }} 
                        />
                      </div>
                    </div>

                    {/* El punto decorativo central con el ícono (solo un círculo moderno) */}
                    <div className={styles.hitoPuntoSticky}>
                      <div 
                        className={styles.hitoPuntoInner} 
                        style={{
                          transform: `scale(${isNearCenter ? 1 : 0.5})`,
                          backgroundColor: isNearCenter ? 'var(--primary-color)' : 'transparent',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          VALORES
      ══════════════════════════════════ */}
      <section className={`${styles.seccion} ${styles.valoresSeccion}`} ref={valoresRef}>
        <div className="container">
          <div className={styles.encabezado}>
            <div className={styles.lineaAcento} />
            <h2 className={styles.seccionTitulo}>Nuestros valores</h2>
            <p className={styles.seccionSub}>
              Los principios que guían cada decisión y cada relación comercial.
            </p>
          </div>

          <div className={styles.valoresInteractivos}>
            <div className={styles.valoresLista}>
              {valores.map((v, i) => (
                <button
                  key={i}
                  className={`${styles.valorTab} ${i === valorActivo ? styles.valorTabActivo : ''}`}
                  onClick={() => setValorActivo(i)}
                >
                  <div className={styles.valorTabIcono}>{v.icono}</div>
                  <span className={styles.valorTabTitulo}>{v.titulo}</span>
                </button>
              ))}
            </div>
            
            <div className={styles.valorContenidoPanel}>
              {/* Usamos key para forzar la animación al cambiar */}
              <div className={styles.valorDetalle} key={valorActivo}>
                <div className={styles.valorIconoDeco}>{valores[valorActivo].icono}</div>
                <h3 className={styles.valorTituloDetalle}>{valores[valorActivo].titulo}</h3>
                <p className={styles.valorTextoDetalle}>{valores[valorActivo].texto}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          MISIÓN Y VISIÓN
      ══════════════════════════════════ */}
      <section className={`${styles.seccion} ${styles.mvSeccion}`} ref={mvRef}>
        <div className="container">
          <div className={styles.encabezado}>
            <div className={styles.lineaAcento} />
            <h2 className={styles.seccionTitulo}>Misión y Visión</h2>
          </div>

          <div className={`${styles.mvGrid} ${mvVisible ? styles.mvVisible : ''}`}>
            <div className={`${styles.mvCard} ${styles.mvMision}`}>
              <div className={styles.mvIcono}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className={styles.mvContenido}>
                <span className={styles.mvLabel}>Nuestra Misión</span>
                <p className={styles.mvTexto}>
                  Desarrollar soluciones integrales de mantenimiento preventivo y correctivo
                  en la industria, comercio y sector servicios, con calidad mundial y soporte
                  continuo a través de nuestra infraestructura, soporte humano calificado y
                  tecnología de punta.
                </p>
                <p className={styles.mvTexto}>
                  Nuestro fin no es solamente proveer la maquinaria necesaria, sino que
                  nuestros clientes cuenten con la asesoría y mantenimiento de ellas, para
                  su buen funcionamiento y desempeño.
                </p>
              </div>
            </div>

            <div className={`${styles.mvCard} ${styles.mvVision}`}>
              <div className={styles.mvIcono}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div className={styles.mvContenido}>
                <span className={styles.mvLabel}>Nuestra Visión</span>
                <p className={styles.mvTexto}>
                  Ser líder en la calidad y efectividad del servicio integral para suministrar
                  productos de ahorro y asesoría industrial a nuestros clientes.
                </p>
                <p className={styles.mvTexto}>
                  Abrirnos brecha a nivel nacional e internacional, con vista hacia
                  Centroamérica, expandiéndonos con paso firme hacia el sur. Sin límites y
                  sin fronteras, dispuestos a alcanzar un mayor renombre por nuestra Guatemala.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          CTA FINAL
      ══════════════════════════════════ */}
      <section className={styles.ctaFinal}>
        <div className={styles.ctaFinalFondo} aria-hidden />
        <div className={`container ${styles.ctaContenido}`}>
          <h2 className={styles.ctaTitulo}>¿Listo para trabajar con nosotros?</h2>
          <p className={styles.ctaSub}>
            Cotiza tus equipos, solicita servicio o visítanos en Guatemala.
          </p>
          <div className={styles.ctaBtns}>
            <Link to="/#contact" className="btn btn-primary">Solicitar cotización</Link>
            <Link to="/#location" className={`btn btn-secondary ${styles.ctaBtnSec}`}>Ver ubicación</Link>
          </div>
        </div>
      </section>

      {/* Modal de Video Ampliado */}
      {videoActivo && (
        <div className={styles.lightbox} onClick={() => setVideoActivo(false)}>
          <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
            <button 
              className={styles.closeBtn} 
              onClick={() => setVideoActivo(false)}
              aria-label="Cerrar video"
            >
              &times;
            </button>
            <div className={styles.iframeModalWrapper}>
              <iframe 
                src={`https://www.youtube.com/embed/-_IN4xbyKwc?autoplay=1&rel=0`} 
                title="Powr Flite Floor Machines Expert Recommendations"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Nosotros;
